import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import NavBar from "../../components/common/NavBar";
import NoData from "../../components/common/NoData";
import DivisionHeader from "../../components/division/DivisionHeader";
import DivisionSummary from "../../components/division/DivisionSummary";
import DivisionTags from "../../components/division/DivisionTags";
import GetDivisionDetail from "../../components/division/queries/GetDivisionDetail";
import ActiveSeason from "../../components/season/ActiveSeason";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { userActions } from "../../store/userAuthSlice";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function DivisionDetailPage() {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const dispatch = useDispatch();

    const [getDivisionDetails] = GetDivisionDetail();
    const { activeProfile } = useActiveProfile();
    const seasonSelectionKey = `${activeProfile?.id}:${params.divSlug}`;
    const selectedSeasonSlug = useSelector(
        (state) => state.user.activeSeasonSelections?.[seasonSelectionKey]
    );

    const [division, setDivision] = useState();
    const [activeSeasonData, setActiveSeasonData] = useState();
    const [games, setGames] = useState([]);
    const [totalGames, setTotalGames] = useState(0);
    const [standings, setStandings] = useState([]);
    const [sanctions, setSanctions] = useState([]);
    const [stages, setStages] = useState([]);
    const [teams, setTeams] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const loadedDivisionKeyRef = useRef(null);

    const [sanctionsPage, setSanctionsPage] = useState(1);
    const [sanctionsTotalPages, setSanctionsTotalPages] = useState(1);

    const waivers = location.state?.waivers || [];

    useEffect(() => {
        const requestKey = `${activeProfile?.id || ""}:${params.divSlug}:${selectedSeasonSlug || ""}`;
        if (loadedDivisionKeyRef.current === requestKey) return;
        loadedDivisionKeyRef.current = requestKey;

        getDivisionDetails({
            variables: {
                divisionSlug: params.divSlug,
                profileId: activeProfile?.id,
            },
        })
            .then((r) => {
                if (r.data?.division) {
                    const divisionData = r.data.division;
                    const availableSeasons = divisionData.seasons || [];
                    const selectedSeason = availableSeasons.find(
                        (season) => season.slug === selectedSeasonSlug
                    );
                    const season = selectedSeason || divisionData.activeSeason || null;

                    setActiveSeasonData(season);
                    setDivision(divisionData);
                    setTeams(season?.teams || []);
                    setSeasons(availableSeasons);
                    setStages(season?.stages || []);
                }
            })
            .catch((err) => {
                loadedDivisionKeyRef.current = null;
                toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                    autoClose: 3000,
                    toastId: "div_detail_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            });
    }, [getDivisionDetails, params.divSlug, activeProfile?.id, selectedSeasonSlug, t]);

    const handleSeasonChange = (season) => {
        dispatch(userActions.selectActiveSeason({
            profileId: activeProfile?.id,
            divisionSlug: params.divSlug,
            seasonSlug: season.slug,
        }));
        setActiveSeasonData(season);
        setStages(season.stages || []);
        setTeams(season.teams || []);
        setGames([]);
        setStandings([]);
        setSanctions([]);
    };

    const navBarElem = {
        pageTitle: t("divisionDetailPage.pageTitle"),
        links: [
            {
                pageTitle: t("divisionDetailPage.breadcrumbOrganizations"),
                navLink: "/management/organizations",
                active: false,
            },
            {
                pageTitle: `${division?.organization?.name || ""} - ${t("divisionDetailPage.breadcrumbDivisions")}`,
                navLink: `/management/organizations/${params.orgSlug}/divisions`,
                active: false,
            },
            {
                pageTitle: division?.name || t("divisionDetailPage.pageTitle"),
                navLink: null,
                active: true,
            },
        ],
    };

    return (
        <>
            <div className="content-wrapper kbola-no-print">
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    <div className="container-fluid">
                        {division && (
                            <DivisionHeader
                                division={division}
                                activeSeasonData={activeSeasonData}
                            />
                        )}

                        <div className="col-md-12">
                            <div className="row">
                                <div className="col-md-4">
                                    <ActiveSeason
                                        seasons={seasons}
                                        setSeasons={setSeasons}
                                        activeSeasonData={activeSeasonData}
                                        setActiveSeasonData={setActiveSeasonData}
                                        setStages={setStages}
                                        setGames={setGames}
                                        setTeams={setTeams}
                                        setStandings={setStandings}
                                        division={division}
                                        setSanctions={setSanctions}
                                        waivers={waivers}
                                        colors={colors}
                                        onSeasonChange={handleSeasonChange}
                                    />
                                </div>

                                <div className="col-md-8">
                                    <DivisionSummary
                                        teamsCount={teams?.length}
                                        activeSeasonData={activeSeasonData}
                                        totalGames={totalGames}
                                        colors={colors}
                                    />
                                </div>
                            </div>
                        </div>

                        {activeSeasonData ? (
                            <DivisionTags
                                stages={stages}
                                setStages={setStages}
                                teams={teams}
                                setTeams={setTeams}
                                activeSeasonData={activeSeasonData}
                                division={division}
                                games={games}
                                setGames={setGames}
                                standings={standings}
                                seasons={seasons}
                                setSeasons={setSeasons}
                                sanctions={{
                                    sanctions,
                                    setSanctions,
                                    sanctionsPage,
                                    setSanctionsPage,
                                    sanctionsTotalPages,
                                    setSanctionsTotalPages,
                                }}
                                waivers={waivers}
                                setTotalGames={setTotalGames}
                                colors={colors}
                            />
                        ) : (
                            <NoData />
                        )}
                    </div>
                </section>
            </div>

            <div id="kbola-print-area" className="kbola-print-area" />
        </>
    );
}
